import { Button, Heading, InputField, RadioButton } from "@/common/components";
import { Constants } from "@/utils/Constants";
import { useCallback, useContext } from "@/common/hooks";
import { ProductContext } from "../ViewProductDetails";

const ProductPageThree = ({ submit }) => {
  const {
    labels,
    onChange,
    errorProps,
    productData,
    setProductData,
    back,
    readOnlyModeForPaymentTableAgeRange,
    addProduct,
    readOnlyAccess,
  } = useContext(ProductContext);

  const radioButtonData = [
    {
      id: "incomeBaseReset",
      data: Constants.incomeBaseReset,
      field: "PRODUCT_BENEFIT_BASE_RESET",
      hide: productData.productType !== "W",
    },
    {
      id: "incomeBaseResetFrequency",
      data: Constants.incomeBaseResetFrequency,
      field: "PRODUCT_BENEFIT_BASE_RESET_FREQUENCY",
      hide: productData.productType !== "W",
    },
    {
      id: "reduceBenefitBase",
      data: Constants.yesOrNo("Reduce Income Base By RMD Excess"),
      field: "PRODUCT_REDUCE_BENEFIT_BASE_BY_RMD_EXCESS",
      hide: productData.productType !== "W",
    },
    {
      id: "weekendHolidayBenefitBaseReset",
      data: Constants.weekendHolidayBenefitBaseReset,
      field: "WEEKEND_HOLIDAY_BENEFIT_BASE_RESET",
      hide: productData.productType !== "W",
    },
    {
      id: "feeSchedule",
      data: Constants.feeSchedule,
      field: "FEE_SCHEDULE",
      hide: productData.productType !== "W",
    },
    {
      id: "benefitBaseResetBasedOnBirthday",
      data: Constants.benefitBaseResetBasedOnBirthday,
      field: "BENEFIT_BASE_RESET_BASED_ON_BIRTHDAY",
      hide: productData.productType !== "W",
      disabled: productData["PRODUCT_BENEFIT_BASE_RESET"] !== "B",
    },
    {
      id: "pendElectionAfterMarketCloses",
      data: Constants.yesOrNo("Pend Election After Market Closes"),
      field: "PEND_ELECTION_AFTER_MARKET_CLOSES",
      hide: productData.productType !== "W",
    },
    {
      id: "processIncomeBaseAndEnrollmentConsentRequired",
      data: Constants.processIncomeBaseAndEnrollmentConsentRequired,
      field: "PROCESS_INCOME_BASE_RESET",
      hide: productData.productType !== "W",
    },
    {
      id: "enrollmentConsentRequired",
      data: Constants.yesOrNo("Consent Required for Enrollment"),
      field: "PRODUCT_ENROLLMENT_CONSENT_REQUIRED_CODE",
    },
    {
      id: "consentRequiredAndContractCertificateProcessing",
      data: Constants.consentRequiredAndContractCertificateProcessing,
      field: "PAYMENT_RATE_AGE_BASIS",
      hide: productData?.productType === "F",
    },
    {
      id: "contractCertificateProcessing",
      data: Constants.yesOrNo(
        "Contract/Certificate Number Processing Required"
      ),
      field: "CONTRACT_CERTIFICATE_NUMBER_PROCESSING_REQUIRED",
    },
    {
      id: "ageCalculationMethod",
      data: Constants.ageCalculationMethod,
      field: "ACCOUNT_CALCULATION_AGE_CODE",
      hide:
        productData?.productType === "I" || productData?.productType === "W",
    },
    {
      id: "gmwbCalculationMethod",
      data: Constants.gmwbCalculationMethod,
      field: "GMWB_CALCULATION_METHOD",
      hide: productData.productType !== "W",
    },
    {
      id: "projectionAndElectionProcessing",
      data: Constants.yesOrNo("Projection and Election Processing Allowed"),
      field: "PROJECTION_AND_ELECTION_PROCESSING_REQUIRED",
      hide: productData.productType !== "W",
    },
    {
      id: "incomeBaseResetAtElection",
      data: Constants.yesOrNo("Reset Income Base at Election"),
      field: "INCOME_BASE_RESET_AT_ELECTION_REQUIRED",
      hide: productData.productType !== "W",
    },
    {
      id: "removeEnrollmentDateAfterLiquidation",
      data: Constants.removeEnrollmentDateAfterLiquidation,
      field: "REMOVE_ENROLLMENT_DATE_AFTER_LIQUIDATION",
      hide: productData.productType !== "W",
    },
    {
      id: "allowMultipleInvestmentVehicles",
      data: Constants.allowMultipleInvestmentVehicles,
      field: "ALLOW_MULTIPLE_INVESTMENT_VEHICLES_REQUIRED",
      hide: productData.productType !== "W",
    },
  ];

  const checkDisabled = useCallback(() => {
    let disable = false;

    if (
      !addProduct &&
      productData?.setupComplete == "true" &&
      productData?.activeDt != ""
    ) {
      let requiredFields = [
        "PRODUCT_MIN_FUNDING_PERIOD",
        "PRODUCT_MIN_PAYMENT_TABLE_AGE_RANGE",
        "PRODUCT_MAX_PAYMENT_TABLE_AGE_RANGE",
      ];

      if (productData?.productType === "F") {
        requiredFields = [
          ...requiredFields,
          "PRODUCT_MIN_PURCHASE_TABLE_AGE_RANGE",
          "PRODUCT_MAX_PURCHASE_TABLE_AGE_RANGE",
        ];
      }

      if (productData?.productType === "W") {
        requiredFields = [
          ...requiredFields,
          "LOCK_OUT_PERIOD_COUNT",
          "MAX_BENEFIT_BASE_RESET_AGE_COUNT",
          "CONTR_LIMIT_ON_TRANSFERS_AND_ROLLOVERS_MAX_AMOUNT",
          "CONTR_LIMIT_ON_TRANSFERS_AND_ROLLOVERS_MONTH_PERIOD",
          "ALLOWABLE_AGE_DIFFERENT_FOR_JOINT_ANNUITANT_YEARS_COUNT",
        ];
        if (productData["PRODUCT_BENEFIT_BASE_RESET_FREQUENCY"] === "O") {
          requiredFields.push("PRODUCT_ONE_TIME_STEP_UP_AGE");
        }
      }

      requiredFields.forEach((e) => {
        if (!productData[e]) {
          disable = true;
        }
      });
    }

    return disable;
  }, [productData]);

  return (
    <section className="container-medium py-5">
      <div className="mb-5">
        <Heading
          text={labels?.product?.schedules}
          wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
          title={false}
        />

        {productData?.productType === "F" && (
          <>
            <div className="fw-bold mb-2">
              {labels?.product?.purchaseTableAge}
            </div>
            <InputField
              id="minimum-purchase"
              label={labels?.product?.minimum}
              type="number"
              max="999"
              wrapperClassName="col-md-2"
              change={(e) =>
                onChange(e, "PRODUCT_MIN_PURCHASE_TABLE_AGE_RANGE")
              }
              {...errorProps("PRODUCT_MIN_PURCHASE_TABLE_AGE_RANGE")}
            />

            <InputField
              id="maximum-purchase"
              label={labels?.product?.maximum}
              type="number"
              max="999"
              wrapperClassName="col-md-2"
              change={(e) =>
                onChange(e, "PRODUCT_MAX_PURCHASE_TABLE_AGE_RANGE")
              }
              {...errorProps("PRODUCT_MAX_PURCHASE_TABLE_AGE_RANGE")}
            />
          </>
        )}

        <>
          <div className="fw-bold mb-2">{labels?.product?.paymentTableAge}</div>
          <InputField
            id="minimum-payment"
            label={labels?.product?.minimum}
            type="number"
            max="999"
            wrapperClassName="col-md-2"
            disabled={!addProduct && readOnlyModeForPaymentTableAgeRange()}
            change={(e) => onChange(e, "PRODUCT_MIN_PAYMENT_TABLE_AGE_RANGE")}
            {...errorProps("PRODUCT_MIN_PAYMENT_TABLE_AGE_RANGE")}
          />

          <InputField
            id="maximum-payment"
            label={labels?.product?.maximum}
            type="number"
            max="999"
            wrapperClassName="col-md-2"
            disabled={!addProduct && readOnlyModeForPaymentTableAgeRange()}
            change={(e) => onChange(e, "PRODUCT_MAX_PAYMENT_TABLE_AGE_RANGE")}
            {...errorProps("PRODUCT_MAX_PAYMENT_TABLE_AGE_RANGE")}
          />
        </>
      </div>

      {productData?.productType === "W" && (
        <InputField
          id="lockout-period"
          label={labels?.product?.lockoutDays}
          type="number"
          max="999"
          wrapperClassName="col-md-6"
          change={(e) => onChange(e, "LOCK_OUT_PERIOD_COUNT")}
          {...errorProps("LOCK_OUT_PERIOD_COUNT")}
        />
      )}

      <InputField
        id="min-funding"
        label={labels?.product?.lockoutYears}
        type="number"
        max="999"
        wrapperClassName="col-md-6"
        change={(e) => onChange(e, "PRODUCT_MIN_FUNDING_PERIOD")}
        {...errorProps("PRODUCT_MIN_FUNDING_PERIOD")}
      />

      {productData?.productType === "F" && (
        <>
          <div className="fw-bold mb-2 mt-4">
            {labels?.product?.exchangeAge}
          </div>
          <InputField
            id="exchange-years"
            label={labels?.product?.years}
            type="number"
            max="999"
            wrapperClassName="col-md-2"
            change={(e) => onChange(e, "EXCHANGE_RESTRICTION_AGE_YEAR_COUNT")}
            {...errorProps("EXCHANGE_RESTRICTION_AGE_YEAR_COUNT")}
          />

          <InputField
            id="exchange-months"
            label={labels?.product?.months}
            type="number"
            max="11"
            wrapperClassName="col-md-2"
            change={(e) => onChange(e, "EXCHANGE_RESTRICTION_AGE_MONTH_COUNT")}
            {...errorProps("EXCHANGE_RESTRICTION_AGE_MONTH_COUNT")}
          />
        </>
      )}

      {productData?.productType === "W" && (
        <InputField
          id="max-benefit-reset"
          label={labels?.product?.maxBenefitReset}
          type="number"
          wrapperClassName="col-md-6"
          change={(e) => onChange(e, "MAX_BENEFIT_BASE_RESET_AGE_COUNT")}
          {...errorProps("MAX_BENEFIT_BASE_RESET_AGE_COUNT")}
        />
      )}

      {productData?.productType === "W" && (
        <>
          <div className="fw-bold mb-2">
            {labels?.product?.contributionLimit}
          </div>
          <div className="d-flex align-items-center">
            <InputField
              id="max-contribution-amount"
              label={labels?.product?.maxAmount}
              type="number"
              showDollarSign={true}
              max="9999999999999999"
              decimal={true}
              wrapperClassName="col-md-6"
              change={(e) =>
                onChange(e, "CONTR_LIMIT_ON_TRANSFERS_AND_ROLLOVERS_MAX_AMOUNT")
              }
              {...errorProps(
                "CONTR_LIMIT_ON_TRANSFERS_AND_ROLLOVERS_MAX_AMOUNT"
              )}
            />

            <strong className="d-block col-md-1 px-2">
              {labels?.product?.per}
            </strong>

            <InputField
              id="contribution-months"
              label={labels?.product?.monthPeriod}
              type="number"
              wrapperClassName="col-md-3"
              change={(e) =>
                onChange(
                  e,
                  "CONTR_LIMIT_ON_TRANSFERS_AND_ROLLOVERS_MONTH_PERIOD"
                )
              }
              {...errorProps(
                "CONTR_LIMIT_ON_TRANSFERS_AND_ROLLOVERS_MONTH_PERIOD"
              )}
            />
          </div>
        </>
      )}

      {productData?.productType === "W" && (
        <InputField
          id="allowable-age-difference"
          label={labels?.product?.allowableAgeDifference}
          type="number"
          wrapperClassName="col-md-6"
          change={(e) =>
            onChange(
              e,
              "ALLOWABLE_AGE_DIFFERENT_FOR_JOINT_ANNUITANT_YEARS_COUNT"
            )
          }
          {...errorProps(
            "ALLOWABLE_AGE_DIFFERENT_FOR_JOINT_ANNUITANT_YEARS_COUNT"
          )}
        />
      )}

      <div className="my-5">
        <Heading
          text={labels?.product?.maintenanceRules}
          wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
          title={false}
        />

        {radioButtonData.map((e) => {
          if (!e.hide)
            return (
              <div key={e.id}>
                <RadioButton
                  wrapperClassName="mb-4"
                  data={e.data}
                  value={productData[e.field]}
                  selected={(e1) =>
                    setProductData({
                      ...productData,
                      [e.field]: e1,
                    })
                  }
                  disabled={e.disabled}
                />
                {e.field === "PRODUCT_BENEFIT_BASE_RESET_FREQUENCY" &&
                  productData?.productType === "W" &&
                  productData["PRODUCT_BENEFIT_BASE_RESET_FREQUENCY"] ===
                    "O" && (
                    <InputField
                      id="step-up-age"
                      label={labels?.product?.stepUp}
                      max="999"
                      type="number"
                      wrapperClassName="col-md-2"
                      change={(e) =>
                        onChange(e, "PRODUCT_ONE_TIME_STEP_UP_AGE")
                      }
                      {...errorProps("PRODUCT_ONE_TIME_STEP_UP_AGE")}
                    />
                  )}
              </div>
            );
        })}
      </div>

      <div className="d-flex justify-content-end">
        <Button
          id="back"
          type="secondary"
          text={labels?.product?.back}
          wrapperClassName="col-md-4 col-6 me-md-2 px-2"
          click={back}
        />
        <Button
          id="submit"
          text={labels?.product?.submit}
          wrapperClassName="col-md-4 col-6"
          click={submit}
          disabled={checkDisabled() || readOnlyAccess}
        />
      </div>
    </section>
  );
};

export default ProductPageThree;
